"use client";

import { formatNumberWithDecimalPlaces } from "@/Utils/formatFunctions";
import { EmptyView } from "@/components/EmptyView/EmptyView";
import { useTrans } from "@/translations";

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export type SortingRateOverTimeData = {
  name: string;
  value: number;
  sortingRate: number;
  percentLabel: string;
  clientSortingRate?: number;
  buildingSortingRate?: number;
};
export default function TenantRecyclingRateOverTimeGraph({
  data,
  isAnimationActive = true,
  terminalName,
  tenantCategoryName,
  showIndustryAverage = true,
}: {
  data: SortingRateOverTimeData[];
  isAnimationActive?: boolean;
  terminalName: string;
  tenantCategoryName: string;
  showIndustryAverage?: boolean;
}) {
  const { t } = useTrans();

  if (!data.find((d) => d.value)) {
    return (
      <div className="flex h-full w-full flex-col justify-stretch">
        <EmptyView type="noDataForInterval" className="h-full" />
      </div>
    );
  }

  // Shift tooltip to begin covering the x axis if terminal name is long.
  // This scales well to over 50 characters in length.
  const averageCharacterWidth = 7; // DMSans at 14px
  const fontSize = 14;
  const supportedNameLength = 15;
  const shift =
    terminalName.length > supportedNameLength
      ? (supportedNameLength - terminalName.length) * averageCharacterWidth
      : 0;

  return (
    <div className="h-full w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 20 }}>
          <XAxis dataKey="name" axisLine={false} tickLine={false} />
          <YAxis type="number" unit={"%"} ticks={[0, 20, 40, 60, 80, 100]} />
          <CartesianGrid stroke="#DAD3D3" vertical={false} />
          <Tooltip
            cursor={{ fill: "#88888844" }}
            formatter={(value) => `${value}%`}
            itemStyle={{ color: "black", fontSize: `${fontSize}px` }}
            labelStyle={{ fontWeight: "bold" }}
            separator=": "
            contentStyle={{ marginLeft: shift }}
          />
          <Bar
            isAnimationActive={isAnimationActive}
            width={20}
            dataKey="sortingRate"
            name={t("circularityLabels:yourRecyclingRate")}
            fill="#E7EB5D"
            type="linear"
            legendType="none"
          >
            <LabelList
              dataKey="sortingRate"
              position="center"
              fill="#333333"
              fontSize="11px"
              formatter={(sortingRate) =>
                formatNumberWithDecimalPlaces(Number(sortingRate), 1) + "%"
              }
            />
          </Bar>
          {!!tenantCategoryName && (
            <>
              <Line
                type="monotone"
                stroke="#FF8736"
                strokeWidth={2}
                dataKey="buildingSortingRate"
                name={t("circularityLabels:buildingSortingRate", {
                  terminalName,
                  tenantCategoryName,
                  interpolation: { escapeValue: false },
                })}
              />
              {showIndustryAverage && (
                <Line
                  type="linear"
                  stroke="#87874E"
                  dataKey="clientSortingRate"
                  strokeWidth={2}
                  name={t("circularityLabels:clientSortingRate", {
                    tenantCategoryName,
                    interpolation: { escapeValue: false },
                  })}
                />
              )}
            </>
          )}
          <Legend
            formatter={(value: string) => (
              <span className="text-black">{value}</span>
            )}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
